import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Table, Project, Submission } from '../../types/domain';
import { apiClient } from '../../services/api';
import { HedgehogSpinner } from '../brand/HedgehogSpinner';
import { PageContainer } from '../layout/PageContainer';
import { Button } from '../ui/Button';
import {
  Zap,
  ShieldCheck,
  ShieldAlert,
  Clock,
  TrendingUp,
  RefreshCw,
  Info,
  HardDrive,
} from 'lucide-react';
import { formatFileSize } from '../../utils/fileUrl';

interface AnalyticsTabProps {
  table: Table;
  project: Project;
  headerActionContainer?: HTMLElement | null;
}

interface RawDataPoint {
  timestamp: string;
  submissions?: number;
  count?: number;
  valid?: number;
  spam?: number;
  errors?: number;
}

interface NormalizedBucket {
  key: string;
  label: string;
  fullDate: string;
  total: number;
  valid: number;
  spam: number;
}

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({
  table,
  project,
  headerActionContainer,
}) => {
  const [timeframe, setTimeframe] = useState<'24h' | '7d'>('24h');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [spamCount, setSpamCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);

  const [p50Latency, setP50Latency] = useState(0);
  const [p90Latency, setP90Latency] = useState(0);
  const [p99Latency, setP99Latency] = useState(0);

  const [storageBytes, setStorageBytes] = useState(table.storage_bytes || 0);
  const [filesCount, setFilesCount] = useState(table.files_count || 0);

  const [rawSeries, setRawSeries] = useState<RawDataPoint[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    if (table.storage_bytes !== undefined) setStorageBytes(table.storage_bytes);
    if (table.files_count !== undefined) setFilesCount(table.files_count);
  }, [table.storage_bytes, table.files_count]);

  const fetchTableAnalytics = useCallback(async (manual = false) => {
    if (manual) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      try {
        const res = await apiClient<{
          table_id: string;
          timeframe?: string;
          total_submissions: number;
          success_count: number;
          spam_count: number;
          error_count: number;
          p50_latency_ms: number;
          p90_latency_ms: number;
          p99_latency_ms: number;
          storage_bytes?: number;
          files_count?: number;
          series?: RawDataPoint[];
        }>(`/projects/${project.id}/tables/${table.id}/analytics?range=${timeframe}`);

        setTotalSubmissions(res.total_submissions || 0);
        setSuccessCount(res.success_count || 0);
        setSpamCount(res.spam_count || 0);
        setErrorCount(res.error_count || 0);
        setP50Latency(res.p50_latency_ms || 0);
        setP90Latency(res.p90_latency_ms || 0);
        setP99Latency(res.p99_latency_ms || 0);
        setStorageBytes(res.storage_bytes ?? table.storage_bytes ?? 0);
        setFilesCount(res.files_count ?? table.files_count ?? 0);

        setRawSeries(res.series || []);
      } catch (err) {
        // Fallback: compute from table rows
        console.warn('Table analytics endpoint fallback to rows calculation:', err);
        const rowsRes = await apiClient<{ data: Submission[]; total: number }>(
          `/projects/${project.id}/tables/${table.id}/rows?limit=100`
        );
        const rows = rowsRes.data || [];
        const total = rowsRes.total || rows.length;
        setTotalSubmissions(total);

        const spam = rows.filter((r) => r.is_spam || r.honeypot_triggered).length;
        setSpamCount(spam);
        setSuccessCount(Math.max(0, total - spam));
        setErrorCount(0);
        setStorageBytes(table.storage_bytes || 0);
        setFilesCount(table.files_count || 0);

        const latencies = rows
          .map((r) => r.metadata?.latency_ms ?? r.metadata?.processing_time_ms ?? 0)
          .filter((lat) => typeof lat === 'number' && lat > 0)
          .sort((a, b) => a - b);

        if (latencies.length > 0) {
          setP50Latency(latencies[Math.floor(latencies.length * 0.5)] || 0);
          setP90Latency(latencies[Math.floor(latencies.length * 0.9)] || 0);
          setP99Latency(latencies[Math.floor(latencies.length * 0.99)] || 0);
        } else {
          setP50Latency(0);
          setP90Latency(0);
          setP99Latency(0);
        }

        setRawSeries([]);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [project.id, table.id, timeframe]);

  useEffect(() => {
    fetchTableAnalytics();
  }, [fetchTableAnalytics]);

  // Normalize backend series data into full timeline buckets
  const normalizedBuckets: NormalizedBucket[] = useMemo(() => {
    const is24h = timeframe === '24h';
    const bucketCount = is24h ? 24 : 7;
    const now = new Date();

    // Map raw points by key for fast lookup
    const rawMap = new Map<string, RawDataPoint>();
    for (const pt of rawSeries) {
      if (!pt.timestamp) continue;
      const d = new Date(pt.timestamp);
      if (isNaN(d.getTime())) continue;
      const key = is24h
        ? `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}T${String(d.getUTCHours()).padStart(2, '0')}`
        : `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
      rawMap.set(key, pt);
    }

    const buckets: NormalizedBucket[] = [];

    if (is24h) {
      // 24 hourly buckets from 23 hours ago up to current hour
      const baseHour = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), 0, 0, 0));
      for (let i = bucketCount - 1; i >= 0; i--) {
        const bucketDate = new Date(baseHour.getTime() - i * 3600000);
        const key = `${bucketDate.getUTCFullYear()}-${String(bucketDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bucketDate.getUTCDate()).padStart(2, '0')}T${String(bucketDate.getUTCHours()).padStart(2, '0')}`;
        const matched = rawMap.get(key);

        const total = matched ? (matched.submissions ?? matched.count ?? 0) : 0;
        const spam = matched ? (matched.spam ?? 0) : 0;
        const valid = matched ? (matched.valid ?? Math.max(0, total - spam)) : 0;

        buckets.push({
          key,
          label: `${String(bucketDate.getHours()).padStart(2, '0')}:00`,
          fullDate: bucketDate.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
          total,
          valid,
          spam,
        });
      }
    } else {
      // 7 daily buckets from 6 days ago up to today
      const baseDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
      for (let i = bucketCount - 1; i >= 0; i--) {
        const bucketDate = new Date(baseDay.getTime() - i * 86400000);
        const key = `${bucketDate.getUTCFullYear()}-${String(bucketDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bucketDate.getUTCDate()).padStart(2, '0')}`;
        const matched = rawMap.get(key);

        const total = matched ? (matched.submissions ?? matched.count ?? 0) : 0;
        const spam = matched ? (matched.spam ?? 0) : 0;
        const valid = matched ? (matched.valid ?? Math.max(0, total - spam)) : 0;

        buckets.push({
          key,
          label: bucketDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          fullDate: bucketDate.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
          total,
          valid,
          spam,
        });
      }
    }

    return buckets;
  }, [timeframe, rawSeries]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <HedgehogSpinner size="md" label="LOADING TABLE TELEMETRY..." />
      </div>
    );
  }

  const rawSpamPct = totalSubmissions > 0 ? (spamCount / totalSubmissions) * 100 : 0;
  const rawSuccessPct = totalSubmissions > 0 ? (successCount / totalSubmissions) * 100 : 100;

  const spamPctDisplay =
    totalSubmissions === 0 || spamCount === 0
      ? '0%'
      : spamCount === totalSubmissions
      ? '100%'
      : rawSpamPct < 1
      ? '< 1%'
      : rawSpamPct > 99
      ? '> 99%'
      : `${Math.round(rawSpamPct)}%`;

  const successPctDisplay =
    totalSubmissions === 0 || successCount === totalSubmissions
      ? '100%'
      : successCount === 0
      ? '0%'
      : rawSuccessPct > 99
      ? '> 99%'
      : rawSuccessPct < 1
      ? '< 1%'
      : `${Math.round(rawSuccessPct)}%`;

  // Chart Geometry
  const svgWidth = 800;
  const svgHeight = 160;
  const paddingBottom = 20;
  const paddingTop = 15;
  const usableHeight = svgHeight - paddingBottom - paddingTop;

  const maxBucketCount = Math.max(1, ...normalizedBuckets.map((b) => b.total));
  const hasData = totalSubmissions > 0 && normalizedBuckets.some((b) => b.total > 0);

  const step = normalizedBuckets.length > 1 ? svgWidth / (normalizedBuckets.length - 1) : svgWidth;

  const validPoints = normalizedBuckets.map((b, i) => {
    const x = i * step;
    const y = hasData
      ? svgHeight - paddingBottom - (b.valid / maxBucketCount) * usableHeight
      : svgHeight - paddingBottom;
    return { x, y, bucket: b };
  });

  const spamPoints = normalizedBuckets.map((b, i) => {
    const x = i * step;
    const y = hasData
      ? svgHeight - paddingBottom - (b.spam / maxBucketCount) * usableHeight
      : svgHeight - paddingBottom;
    return { x, y, bucket: b };
  });

  const validLinePath = validPoints.length > 0
    ? validPoints.reduce((acc, pt, i) => (i === 0 ? `M ${pt.x} ${pt.y}` : `${acc} L ${pt.x} ${pt.y}`), '')
    : '';

  const validAreaPath = validPoints.length > 0
    ? `${validLinePath} L ${svgWidth} ${svgHeight - paddingBottom} L 0 ${svgHeight - paddingBottom} Z`
    : '';

  const spamLinePath = spamPoints.length > 0 && spamCount > 0
    ? spamPoints.reduce((acc, pt, i) => (i === 0 ? `M ${pt.x} ${pt.y}` : `${acc} L ${pt.x} ${pt.y}`), '')
    : '';

  const activeHoveredBucket = hoveredIndex !== null ? normalizedBuckets[hoveredIndex] : null;

  return (
    <PageContainer variant="tab">
      {/* Portaled Header Controls */}
      {headerActionContainer
        ? createPortal(
            <div className="flex items-center gap-2">
              <div className="flex items-center p-0.5 rounded bg-cf-surface border border-cf-border text-xs gap-0.5">
                <Button
                  type="button"
                  variant={timeframe === '24h' ? 'secondary' : 'ghost'}
                  size="xs"
                  onClick={() => setTimeframe('24h')}
                  className={timeframe === '24h' ? 'text-cf-orange font-semibold shadow-xs' : ''}
                >
                  24 Hours
                </Button>
                <Button
                  type="button"
                  variant={timeframe === '7d' ? 'secondary' : 'ghost'}
                  size="xs"
                  onClick={() => setTimeframe('7d')}
                  className={timeframe === '7d' ? 'text-cf-orange font-semibold shadow-xs' : ''}
                >
                  7 Days
                </Button>
              </div>

              <Button
                type="button"
                variant="secondary"
                size="icon-xs"
                onClick={() => fetchTableAnalytics(true)}
                disabled={isRefreshing}
                title="Refresh metrics"
                aria-label="Refresh metrics"
              >
                <RefreshCw size={13} className={isRefreshing ? 'animate-spin text-cf-orange' : ''} />
              </Button>
            </div>,
            headerActionContainer
          )
        : null}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Total Ingested */}
        <div className="p-3.5 bg-cf-surface border border-cf-border rounded-lg shadow-sm">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs uppercase mb-1">
            <span>Total Captured</span>
            <TrendingUp size={14} className="text-cf-orange" />
          </div>
          <div className="text-2xl font-bold text-cf-textPrimary tabular-nums">
            {totalSubmissions.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-cf-textMuted">
            {timeframe === '24h' ? 'Last 24 hours' : 'Last 7 days'}
          </div>
        </div>

        {/* Valid Submissions */}
        <div className="p-3.5 bg-cf-surface border border-cf-border rounded-lg shadow-sm">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs uppercase mb-1">
            <span>Verified Valid</span>
            <ShieldCheck size={14} className="text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-emerald-400 tabular-nums">
            {successCount.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-cf-textMuted">
            {successPctDisplay} acceptance rate
          </div>
        </div>

        {/* Spam Trapped */}
        <div className="p-3.5 bg-cf-surface border border-cf-border rounded-lg shadow-sm">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs uppercase mb-1">
            <span>Spam Quarantined</span>
            <ShieldAlert size={14} className="text-rose-400" />
          </div>
          <div className="text-2xl font-bold text-rose-400 tabular-nums">
            {spamCount.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-cf-textMuted">
            {spamPctDisplay} bot honeypot hits
          </div>
        </div>

        {/* Ingest Latency */}
        <div className="p-3.5 bg-cf-surface border border-cf-border rounded-lg shadow-sm">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs uppercase mb-1">
            <span>Ingest Latency (P50)</span>
            <Zap size={14} className="text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-emerald-400 tabular-nums">
            {p50Latency > 0 ? Number(p50Latency).toFixed(2) : '0.00'} ms
          </div>
          <div className="mt-1 text-xs text-emerald-400/80">
            P99: {p99Latency > 0 ? Number(p99Latency).toFixed(2) : '0.00'} ms
          </div>
        </div>

        {/* Datastore Storage */}
        <div className="p-3.5 bg-cf-surface border border-cf-border rounded-lg shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs uppercase mb-1">
            <span>Datastore Storage</span>
            <HardDrive size={14} className="text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-cf-textPrimary tabular-nums">
            {formatFileSize(storageBytes)}
          </div>
          <div className="mt-1 text-xs text-cf-textMuted flex items-center justify-between">
            <span>{filesCount.toLocaleString()} {filesCount === 1 ? 'file' : 'files'}</span>
            <span>Uploaded Assets</span>
          </div>
        </div>
      </div>

      {/* High-Performance SVG Area Chart */}
      <div className="p-4 bg-cf-surface border border-cf-border rounded-lg shadow-sm space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-cf-textPrimary">
              Ingestion Volume Timeline ({timeframe === '24h' ? '24 Hours' : '7 Days'})
            </span>
            {hasData && (
              <span className="text-xs text-cf-textMuted font-mono">
                Peak: {maxBucketCount.toLocaleString()} req / interval
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <span className="text-cf-textSecondary">Valid Traffic</span>
            </div>
            {spamCount > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                <span className="text-cf-textSecondary">Spam Quarantined</span>
              </div>
            )}
          </div>
        </div>

        <div className="w-full relative">
          <div className="w-full h-44 bg-cf-bg rounded border border-cf-border relative overflow-hidden">
            <svg
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              preserveAspectRatio="none"
              className="w-full h-full"
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <defs>
                <linearGradient id="tableTelemGradValid" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#10B981" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Grid horizontal hairline lines */}
              <line
                x1="0"
                y1={paddingTop}
                x2={svgWidth}
                y2={paddingTop}
                stroke="currentColor"
                className="text-cf-border"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <line
                x1="0"
                y1={paddingTop + usableHeight * 0.5}
                x2={svgWidth}
                y2={paddingTop + usableHeight * 0.5}
                stroke="currentColor"
                className="text-cf-border"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <line
                x1="0"
                y1={svgHeight - paddingBottom}
                x2={svgWidth}
                y2={svgHeight - paddingBottom}
                stroke="currentColor"
                className="text-cf-border"
                strokeWidth="1"
              />

              {/* Chart Paths when data exists */}
              {hasData && (
                <>
                  {validAreaPath && <path d={validAreaPath} fill="url(#tableTelemGradValid)" />}
                  {validLinePath && (
                    <path
                      d={validLinePath}
                      fill="none"
                      stroke="#10B981"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                  {spamLinePath && (
                    <path
                      d={spamLinePath}
                      fill="none"
                      stroke="#EF4444"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}

                  {/* Interactive point markers */}
                  {validPoints.map((pt, idx) => (
                    <g key={pt.bucket.key}>
                      {pt.bucket.total > 0 && (
                        <circle
                          cx={pt.x}
                          cy={pt.y}
                          r={hoveredIndex === idx ? 4.5 : 3}
                          fill="#10B981"
                          stroke="#0F172A"
                          strokeWidth="1.5"
                          className="transition-all duration-150"
                        />
                      )}
                    </g>
                  ))}
                </>
              )}

              {/* Transparent hit columns for hover interaction */}
              {normalizedBuckets.map((_, idx) => {
                const colWidth = svgWidth / normalizedBuckets.length;
                const colX = idx * colWidth;
                return (
                  <rect
                    key={`hit-${idx}`}
                    x={colX}
                    y={0}
                    width={colWidth}
                    height={svgHeight}
                    fill="transparent"
                    className="cursor-pointer"
                    onMouseEnter={() => setHoveredIndex(idx)}
                  />
                );
              })}

              {/* Hover vertical hairline cursor */}
              {hoveredIndex !== null && (
                <line
                  x1={hoveredIndex * step}
                  y1={paddingTop}
                  x2={hoveredIndex * step}
                  y2={svgHeight - paddingBottom}
                  stroke="#F97316"
                  strokeWidth="1.5"
                  strokeDasharray="2 2"
                />
              )}
            </svg>

            {/* Zero-traffic empty state overlay */}
            {!hasData && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center pointer-events-none">
                <Info size={20} className="text-cf-textMuted mb-1.5 opacity-60" />
                <p className="text-xs font-semibold text-cf-textSecondary">
                  No submissions recorded in the past {timeframe === '24h' ? '24 hours' : '7 days'}
                </p>
                <p className="text-xs text-cf-textMuted max-w-sm mt-0.5">
                  The telemetry pipeline is standing by. Submit form data or post webhooks to populate real-time volume and latency percentiles.
                </p>
              </div>
            )}

            {/* Hover Tooltip Card */}
            {activeHoveredBucket && (
              <div
                className="absolute top-2 pointer-events-none z-10 p-2 rounded bg-cf-surface border border-cf-border shadow-lg text-xs"
                style={{
                  left: `${Math.min(85, Math.max(5, (hoveredIndex! / (normalizedBuckets.length - 1)) * 100))}%`,
                  transform: 'translateX(-50%)',
                }}
              >
                <div className="font-semibold text-cf-textPrimary mb-1">
                  {activeHoveredBucket.fullDate}
                </div>
                <div className="space-y-0.5 text-xs">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-cf-textSecondary">Total:</span>
                    <span className="font-semibold text-cf-textPrimary tabular-nums">
                      {activeHoveredBucket.total.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-emerald-400">Valid:</span>
                    <span className="font-semibold text-emerald-400 tabular-nums">
                      {activeHoveredBucket.valid.toLocaleString()}
                    </span>
                  </div>
                  {activeHoveredBucket.spam > 0 && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-rose-400">Spam:</span>
                      <span className="font-semibold text-rose-400 tabular-nums">
                        {activeHoveredBucket.spam.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* X-Axis labels */}
          <div className="flex justify-between text-xs text-cf-textMuted mt-1.5 px-1 select-none">
            {timeframe === '24h' ? (
              <>
                <span>24h ago</span>
                <span>18h ago</span>
                <span>12h ago</span>
                <span>6h ago</span>
                <span>Now</span>
              </>
            ) : (
              normalizedBuckets.map((b, i) => (
                <span key={b.key} className={i % 2 === 1 ? 'hidden sm:inline' : 'inline'}>
                  {b.label}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Latency Percentiles Breakdown */}
      <div className="p-4 bg-cf-surface border border-cf-border rounded-lg shadow-sm">
        <h3 className="text-xs font-semibold text-cf-textPrimary mb-3 flex items-center gap-1.5">
          <Clock size={14} className="text-cf-orange" />
          <span>Processing Latency Percentiles Breakdown</span>
        </h3>

        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="p-3 bg-cf-bg border border-cf-border rounded">
            <span className="block text-xs uppercase text-cf-textMuted">
              P50 (Median)
            </span>
            <span className="text-lg font-bold text-emerald-400 tabular-nums">
              {p50Latency > 0 ? Number(p50Latency).toFixed(2) : '0.00'} ms
            </span>
          </div>

          <div className="p-3 bg-cf-bg border border-cf-border rounded">
            <span className="block text-xs uppercase text-cf-textMuted">
              P90 (Tail)
            </span>
            <span className="text-lg font-bold text-cf-textPrimary tabular-nums">
              {p90Latency > 0 ? Number(p90Latency).toFixed(2) : '0.00'} ms
            </span>
          </div>

          <div className="p-3 bg-cf-bg border border-cf-border rounded">
            <span className="block text-xs uppercase text-cf-textMuted">
              P99 (Extreme)
            </span>
            <span className="text-lg font-bold text-cf-textPrimary tabular-nums">
              {p99Latency > 0 ? Number(p99Latency).toFixed(2) : '0.00'} ms
            </span>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};
